import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ArrowLeft, FileText, FolderOpen, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@auw/workspace-ui';
import { getSkillSourceFile, listSkillSourceFiles, saveSkillSourceFile, type SkillSourceFile, type SkillSourceKind } from '@auw/api-client';

export interface SkillSourceTarget { id: string; name: string; kind: SkillSourceKind }
interface Props {
  target: SkillSourceTarget;
  onBack: () => void;
  onStateChange: (state: { dirty: boolean; saving: boolean }) => void;
  onSaved: () => void;
}

interface FileNode { name: string; path: string; children?: FileNode[] }
function buildTree(paths: string[]): FileNode[] {
  const nodes: FileNode[] = [];
  for (const path of paths) {
    const parts = path.split('/');
    let level = nodes;
    parts.forEach((name, i) => {
      const key = parts.slice(0, i + 1).join('/');
      let node = level.find((item) => item.path === key);
      if (!node) { node = { name, path: key, ...(i < parts.length - 1 ? { children: [] } : {}) }; level.push(node); }
      if (node.children) level = node.children;
    });
  }
  return nodes;
}

export default function SkillSourcePanel({ target, onBack, onStateChange, onSaved }: Props) {
  const { t } = useTranslation('common');
  const [files, setFiles] = useState<string[]>([]);
  const [selected, setSelected] = useState('skill.md');
  const [cache, setCache] = useState<Record<string, SkillSourceFile>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [retry, setRetry] = useState(0);
  /* 내 스킬만 편집한다. 레퍼런스(official)·사내 공유(shared) 스킬은 읽기 전용. */
  const readOnly = target.kind !== 'user';
  const file = cache[selected];
  const dirtyPaths = Object.keys(drafts).filter((path) => drafts[path] !== cache[path]?.content);
  const dirty = dirtyPaths.length > 0;
  const tree = useMemo(() => buildTree(files), [files]);

  useEffect(() => { onStateChange({ dirty, saving }); }, [dirty, saving, onStateChange]);
  useEffect(() => {
    if (!dirty) return;
    const guard = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(''); setSaved(false);
    async function load() {
      try {
        const [manifest, content] = await Promise.all([
          listSkillSourceFiles(target.kind, target.id),
          file ? Promise.resolve(file) : getSkillSourceFile(target.kind, target.id, selected),
        ]);
        if (cancelled) return;
        setFiles(manifest.files);
        setCache((prev) => ({ ...prev, [selected]: content }));
      } catch (err) { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); }
      finally { if (!cancelled) setLoading(false); }
    }
    void load();
    return () => { cancelled = true; };
    // Cache fills must not restart an in-flight selection request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.id, target.kind, selected, retry]);

  async function save() {
    if (!file || readOnly || !file.editable || saving) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      const result = await saveSkillSourceFile(target.id, { path: selected, content: drafts[selected] ?? file.content, sha256: file.sha256 });
      setCache((prev) => ({ ...prev, [selected]: result }));
      setDrafts((prev) => { const next = { ...prev }; delete next[selected]; return next; });
      setSaved(true); onSaved();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setSaving(false); }
  }

  function renderNodes(nodes: FileNode[]): React.ReactNode {
    return <TreeList>{nodes.map((node) => <li key={node.path}>
      {node.children ? <details open><summary><FolderOpen size={15} />{node.name}</summary>{renderNodes(node.children)}</details> :
        <FileButton type="button" $active={selected === node.path} aria-current={selected === node.path ? 'page' : undefined}
          title={node.path} disabled={saving} onClick={() => setSelected(node.path)}>
          <FileText size={15} /><span>{node.name}</span>{dirtyPaths.includes(node.path) && <span aria-label={t('skill.source.unsaved')}>•</span>}
        </FileButton>}
    </li>)}</TreeList>;
  }

  return <Panel>
    <Header>
      <Button type="button" variant="surface" size="sm" leftIcon={<ArrowLeft size={15} />} onClick={onBack} disabled={saving}>{t('skill.detailBack')}</Button>
      <Title>{target.name}</Title>
      <Hint>{readOnly ? t('skill.source.readOnly') : t('skill.edit')}</Hint>
    </Header>
    {error && <ErrorBox role="alert">{error} {!file && <Button type="button" size="sm" variant="surface" onClick={() => setRetry((v) => v + 1)}>{t('skill.source.retry')}</Button>}</ErrorBox>}
    <Workspace>
      <FileNav aria-label={t('skill.source.files')}>
        <Label>{t('skill.source.files')}</Label>
        {renderNodes(tree)}
      </FileNav>
      <Content>
        <Toolbar><FilePath>{selected}</FilePath>{!readOnly && file?.editable &&
          <Button type="button" size="sm" variant="surface" leftIcon={<Save size={14} />} disabled={saving || loading || !dirtyPaths.includes(selected)} onClick={() => void save()}>
            {saving ? t('skill.source.saving') : t('skill.source.save')}
          </Button>}
        </Toolbar>
        {loading ? <Notice role="status">{t('skill.source.loading')}</Notice> : file ? (
          file.binary ? <Notice>{t('skill.source.binary')}</Notice> :
          <Editor aria-label={t('skill.source.content')} value={drafts[selected] ?? file.content ?? ''}
            readOnly={readOnly || !file.editable || saving} spellCheck={false}
            onChange={(event) => { setDrafts((prev) => ({ ...prev, [selected]: event.target.value })); setSaved(false); }} />
        ) : null}
        <Status role="status">{saved ? t('skill.source.saved') : readOnly ? t(target.kind === 'shared' ? 'skill.sharedReadOnlyHint' : 'skill.source.readOnlyHint') : t('skill.source.editHint')}</Status>
      </Content>
    </Workspace>
  </Panel>;
}

const Panel = styled.div`display:flex; flex-direction:column; flex:1; min-height:0; padding:20px; gap:14px;`;
const Header = styled.div`display:flex; align-items:center; gap:12px; flex-shrink:0;`;
const Title = styled.h3`margin:0; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:15px; color:${({ theme }) => theme.colors.textPrimary};`;
const Hint = styled.span`font-size:12px; color:${({ theme }) => theme.colors.textMuted};`;
const Workspace = styled.div`display:flex; flex:1; min-height:0; border:1px solid ${({ theme }) => theme.colors.border}; border-radius:10px; overflow:hidden; @media(max-width:640px){flex-direction:column;}`;
const FileNav = styled.nav`width:240px; flex-shrink:0; overflow:auto; padding:12px 8px; border-right:1px solid ${({ theme }) => theme.colors.border}; @media(max-width:640px){width:auto; max-height:150px; border-right:0; border-bottom:1px solid ${({ theme }) => theme.colors.border};}`;
const Label = styled.div`padding:0 8px 10px; font-size:12px; font-weight:600; color:${({ theme }) => theme.colors.textSecondary};`;
const TreeList = styled.ul`list-style:none; padding:0; margin:0; ul{padding-left:14px;} summary{display:flex; align-items:center; gap:7px; cursor:pointer; padding:7px; font-size:12px; color:${({ theme }) => theme.colors.textSecondary};} svg{flex-shrink:0;}`;
const FileButton = styled.button<{ $active: boolean }>`display:flex; align-items:center; gap:7px; width:100%; padding:8px; border:0; border-radius:5px; cursor:pointer; text-align:left; font-size:12px; background:${({ theme, $active }) => $active ? theme.colors.bgHover : 'transparent'}; color:${({ theme }) => theme.colors.textPrimary}; span:first-of-type{overflow:hidden; text-overflow:ellipsis; white-space:nowrap;} &:hover{background:${({ theme }) => theme.colors.bgHover};} &:focus-visible{outline:2px solid currentColor; outline-offset:-2px;}`;
const Content = styled.div`display:flex; flex-direction:column; flex:1; min-width:0; min-height:0;`;
const Toolbar = styled.div`display:flex; align-items:center; justify-content:space-between; gap:12px; min-height:48px; padding:8px 12px; border-bottom:1px solid ${({ theme }) => theme.colors.border};`;
const FilePath = styled.span`font:12px monospace; overflow-wrap:anywhere; color:${({ theme }) => theme.colors.textSecondary};`;
const Editor = styled.textarea`flex:1; width:100%; min-height:0; box-sizing:border-box; resize:none; border:0; outline:none; padding:16px; background:${({ theme }) => theme.colors.bgPrimary}; color:${({ theme }) => theme.colors.textPrimary}; font:13px/1.7 ui-monospace, SFMono-Regular, Consolas, monospace; tab-size:2; &:focus-visible{box-shadow:inset 0 0 0 2px ${({ theme }) => theme.colors.border};}`;
const Notice = styled.div`padding:24px; flex:1; font-size:13px; color:${({ theme }) => theme.colors.textSecondary};`;
const Status = styled.div`padding:8px 12px; font-size:11px; color:${({ theme }) => theme.colors.textMuted}; border-top:1px solid ${({ theme }) => theme.colors.border};`;
const ErrorBox = styled.div`display:flex; align-items:center; gap:12px; font-size:12px; color:${({ theme }) => theme.colors.error};`;
